import type { PivotConfig } from './types';
import { toFiniteSeriesNumber } from './toFiniteSeriesNumber';

/**
 * Pivot long-form rows to wide. With `valueMap`, unmapped dimension values
 * are dropped (rogue-series guard). Duplicate (dimension, index) pairs:
 * last write wins.
 */
export function pivotLongFormToWide(
  rows: Record<string, unknown>[],
  opts: PivotConfig,
): Record<string, unknown>[] {
  const { dimension, indexBy, measure, valueMap, sortIndex, displayBy } = opts;
  const allowed = valueMap ? new Set(Object.keys(valueMap)) : null;
  const grouped = new Map<unknown, Record<string, unknown>>();

  for (const row of rows) {
    const dimRaw = row[dimension];
    const dimKey = String(dimRaw);
    if (allowed && !allowed.has(dimKey)) continue;
    const seriesKey = valueMap ? valueMap[dimKey] : dimKey;
    const idxValue = row[indexBy];
    const acc = grouped.get(idxValue) ?? { [indexBy]: idxValue };
    acc[seriesKey] = toFiniteSeriesNumber(row[measure]) ?? row[measure];
    if (displayBy !== undefined) {
      if (Object.prototype.hasOwnProperty.call(row, displayBy)) {
        acc[displayBy] = row[displayBy];
      }
    }
    grouped.set(idxValue, acc);
  }

  const wide = Array.from(grouped.values());
  if (!sortIndex) return wide;

  const direction = sortIndex === 'desc' ? -1 : 1;
  return wide.sort((a, b) => {
    const av = a[indexBy];
    const bv = b[indexBy];
    const an = typeof av === 'number' ? av : Number(av);
    const bn = typeof bv === 'number' ? bv : Number(bv);
    if (Number.isFinite(an) && Number.isFinite(bn)) {
      return (an - bn) * direction;
    }
    return String(av).localeCompare(String(bv)) * direction;
  });
}
